import {useEffect, useState} from 'react';
import type {
  Task,
  TaskStatus,
  UpdateTaskInput,
} from '@entities/task/model/types';

/** Редактируемые поля формы, без серверных прав и комментариев. */
export interface TaskDraftFields {
  title: string;
  text: string;
  managerId: string;
  priority: Task['priority'];
  status: TaskStatus;
  dueDate: string;
  callId: string;
}

/** Текущие серверные значения формы; незатронутые поля не копируются в state. */
export const taskDraftFields = (task?: Task): TaskDraftFields => ({
  title: task?.title ?? '',
  text: task?.text ?? '',
  managerId: task?.managerId ?? '',
  priority: task?.priority ?? 'medium',
  status: task?.status ?? 'planned',
  dueDate: task?.dueDate ?? '',
  callId: task?.communicationId ?? '',
});

/** PATCH только ручных изменений; исполнитель отправляет исключительно статус. */
export const taskDraftUpdate = (
  task: Task,
  edits: Partial<TaskDraftFields>,
  canEdit: boolean,
  overrideStatus?: TaskStatus,
): UpdateTaskInput => {
  const input: UpdateTaskInput = {};
  const nextStatus = overrideStatus ?? edits.status;
  if (nextStatus !== undefined && nextStatus !== task.status)
    input.status = nextStatus;
  if (!canEdit) return input;
  if (edits.title !== undefined && edits.title.trim() !== task.title)
    input.title = edits.title.trim();
  if (edits.text !== undefined && edits.text.trim() !== (task.text ?? ''))
    input.text = edits.text.trim();
  if (edits.priority !== undefined && edits.priority !== task.priority)
    input.priority = edits.priority;
  if (edits.dueDate !== undefined && edits.dueDate !== task.dueDate)
    input.dueDate = edits.dueDate;
  if (
    edits.managerId !== undefined &&
    edits.managerId !== (task.managerId ?? '')
  )
    input.managerId = edits.managerId || null;
  if (
    edits.callId !== undefined &&
    edits.callId !== (task.communicationId ?? '')
  )
    input.communicationId = edits.callId || null;
  return input;
};

/** Черновик поверх свежего ответа: refetch обновляет только нетронутые поля. */
export const useTaskDraft = (task: Task | undefined, open: boolean) => {
  const [state, setState] = useState<{
    taskId?: string;
    edits: Partial<TaskDraftFields>;
  }>({edits: {}});
  const edits = open && task && state.taskId === task.id ? state.edits : {};
  const fields = {...taskDraftFields(task), ...edits};
  useEffect(() => {
    if (!open) setState({edits: {}});
  }, [open]);
  const setField = <K extends keyof TaskDraftFields>(
    key: K,
    value: TaskDraftFields[K],
  ) => {
    if (!task) return;
    setState((previous) => {
      const next = {...(previous.taskId === task.id ? previous.edits : {})};
      if (value === taskDraftFields(task)[key]) delete next[key];
      else next[key] = value;
      return {taskId: task.id, edits: next};
    });
  };
  const getUpdate = (canEdit: boolean, overrideStatus?: TaskStatus) =>
    task ? taskDraftUpdate(task, edits, canEdit, overrideStatus) : {};
  return {
    fields,
    setField,
    getUpdate,
    isDirty: Object.keys(getUpdate(true)).length > 0,
  };
};
